import request from "@/api";
import { BaseService } from "@/api/services/baseService";
import type { Project, ProjectSpecialVo, ProjectVo, ProjectExtend } from "@zhihaoscm/entity";
import type { Page } from "@zhihaoscm/entity";
import { confirmable } from "@/utils/decorator/confirmable";
import type {
    FindBySupplierAndSupplyChainParams,
    ProjectCreateForm,
    ProjectExtendForm,
    ProjectJoinForm,
    ProjectQueryParam,
    ProjectSelectorQueryParam,
    SettleParams,
    UpdateServiceCostParams
} from "@/entity/purchasing/project";
import type { GenerateProjectParams } from "@/entity/purchasing/intend";

// 项目管理
class ProjectService extends BaseService<ProjectVo, ProjectVo, ProjectQueryParam, ProjectCreateForm> {
    // 企业定制分页查询商品列表
    public async enterprisePaging(params: ProjectQueryParam): Promise<Page<ProjectVo>> {
        const { data } = await this.service.get(`${this.path}/enterpriseCustomPaging`, { params });
        return data;
    }
    public async paging(params: ProjectQueryParam): Promise<Page<ProjectVo>> {
        const { data } = await this.service.get(`${this.path}/paging`, { params });
        return data;
    }
    public async listSelector(params: ProjectSelectorQueryParam): Promise<Page<Project>> {
        const { data } = await this.service.get(`${this.path}/selector`, { params });
        return data;
    }
    // 开始
    @confirmable("是否开始")
    public async start(id: number): Promise<void> {
        await this.service.put(`${this.path}/start/${id}`);
    }
    // 指派
    public async assign(id: number, userId: number): Promise<void> {
        await this.service.put(`${this.path}/assign/${id}/${userId}`);
    }
    // 完成
    public async finish(id: number): Promise<void> {
        await this.service.put(`${this.path}/finish/${id}`);
    }
    // 关闭
    public async close(id: number, reset: boolean): Promise<void> {
        await this.service.put(`${this.path}/close/${id}?reset=${reset}`);
    }
    // 获取项目专员列表
    public async getSpecialsSelector(keyword: string): Promise<ProjectSpecialVo[]> {
        const { data } = await this.service.get(`${this.path}/project-specials/selector?name=${keyword}`);
        return data;
    }

    async getProjectById(id: number) {
        const { data } = await this.service.get<ProjectVo>(`${this.path}/vo/${id}`);
        return data;
    }
    public async updatePrice(form: any) {
        const { data } = await this.service.put(`${this.path}/update-price`, form);
        return data;
    }
    @confirmable("是否提交")
    async generateProject(params: GenerateProjectParams) {
        await this.service.post<ProjectVo>(`${this.path}/generate-project`, params);
    }
    async updateServiceCost(params: UpdateServiceCostParams) {
        await this.service.put<ProjectVo>(`${this.path}/update/service-cost`, params);
    }
    async settle(params: SettleParams) {
        await this.service.post<ProjectVo>(`${this.path}/settle`, params);
    }
    // 获取可提货吨数
    async findEstimateTon(params: { projectId: number; customId: number; roleCode: number }) {
        const { data } = await this.service.get<number>(`${this.path}/findEstimateTon`, { params });
        return data;
    }
    // 获取可提货金额
    async findEstimateAmount(projectId: string, customId: string, roleCode: number) {
        const { data } = await this.service.get<number>(
            `${this.path}/findEstimatedAmount?projectId=${projectId}&customId=${customId}&roleCode=${roleCode}`
        );
        return data;
    }
    @confirmable("是否提交")
    public async edit(id: string, params: ProjectCreateForm): Promise<void> {
        await this.service.put(`${this.path}/${id}`, params);
    }
    @confirmable("是否撤回，撤回后除基本信息以外的数据将清除")
    public async revoke(id: string | number): Promise<void> {
        await this.service.put(`${this.path}/revoke/${id}`);
    }
    @confirmable("完成确认，完成后，项目可以添加单据信息。")
    public async offLineFinish(id: number): Promise<void> {
        await this.service.put(`${this.path}/finish/${id}`);
    }
    @confirmable("是否删除，删除后该录入的项目详情将不再展示")
    public async del(id: number | string) {
        const { data } = await this.service.delete(`${this.path}/${id}`);
        return data;
    }
    /**根据供应商和供应链查询项目列表 项目状态不为已完成和已关闭 */
    public async findBySupplierAndSupplyChain(params: FindBySupplierAndSupplyChainParams) {
        const { data } = await this.service.get(`${this.path}/findBySupplierAndSupplyChain`, { params });
        return data as Project[];
    }

    /**加入项目 */
    @confirmable("是否加入该项目")
    public async joinProject(form: ProjectJoinForm) {
        const { data } = await this.service.post(`${this.path}/join-project`, form);
        return data;
    }

    /**添加采购商 */
    @confirmable("是否添加采购商")
    public async addBuyer(forms: ProjectExtendForm[]) {
        const { data } = await this.service.post(`${this.path}/addBuyer`, forms);
        return data;
    }

    /**查询采购商信息 */
    public async getBuyers(projectId: number) {
        const { data } = await this.service.get(`${this.path}/buyers`, { params: { projectId } });
        return data as Array<ProjectExtend>;
    }

    @confirmable("是否下载？将下载导出文件，请确认")
    public async projectLedgerExport(id: number): Promise<Blob> {
        return await this.service.get(`${this.path}/projectLedgerExport/${id}`, {
            responseType: "blob",
            timeout: 600000
        });
    }
}
export const projectService = new ProjectService("/admin/project", request.service);
